/* eslint tap/no-raw-text-js: 0 */ // --> OFF
import _, { forEach, map, first, isArray, isEmpty, find } from 'lodash';
import NumeralService from '@/modules/core/app/services/NumeralService';
import { MapboxRenderService } from '@/modules/ta/legacy/widget/services/types/MapboxRenderService';
import {
  BLUE,
  MAPBOX_LAYERS,
  SYMBOL,
  MAPBOX_LAYERS_METADATA,
  COUNTRY,
  STATE,
} from '@/modules/ta/legacy/widget/mapbox.constants';
import { MapboxFeatureBoundsHandler } from '@/modules/ta/legacy/widget/services/types/MapboxAutoZoomService';
import { CONGRESSIONAL_DISTRICT } from '@/modules/ta/widget/mapbox.constants';
import MapboxTileSetStorage from '@/modules/ta/legacy/widget/services/types/MapboxTileSetStorage';

export const MapboxMixin = {
  props: {
    id: {
      type: String,
      required: true,
    },
    isDashboard: {
      type: Boolean,
      default: true,
    },
    token: {
      type: String,
      required: true,
    },
    widget: {
      type: Object,
      required: true,
    },
    isExporting: {
      type: Boolean,
      required: true,
    },
    chartPalette: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    scaleControl: {
      type: Object,
      default: () => ({
        show: true,
        position: 'top-left',
      }),
    },
    fullscreenControl: {
      type: Object,
      default: () => ({
        show: true,
        position: 'top-left',
      }),
    },
    geoLocateControl: {
      type: Object,
      default: () => ({
        show: true,
        position: 'top-left',
      }),
    },
    zoomLevel: {
      type: Number,
      default: 4,
    },
    centerPosition: {
      type: Array,
      default: () => [-96, 37.8],
    },
  },
  computed: {
    latLongMetricSource() {
      return this.renderService.getUniqueId('latlongmetricsource', this.id);
    },
    latLongSource() {
      return this.renderService.getUniqueId('latlongsource', this.id);
    },
    latLongUnClusterLayer() {
      return this.renderService.getUniqueId('latlongunclusterlayer', this.id);
    },
    symbolLayer() {
      return this.renderService.getUniqueId('symbollayer', this.id);
    },
    countrySource() {
      return this.renderService.getUniqueId('countrysource', this.id);
    },
    countryLayer() {
      return this.renderService.getUniqueId('countrylayer', this.id);
    },
    stateSource() {
      return this.renderService.getUniqueId('statesource', this.id);
    },
    stateLayer() {
      return this.renderService.getUniqueId('statelayer', this.id);
    },
    citySource() {
      return this.renderService.getUniqueId('citysource', this.id);
    },
    cityLayer() {
      return this.renderService.getUniqueId('citylayer', this.id);
    },
    countySource() {
      return this.renderService.getUniqueId('countysource', this.id);
    },
    countyLayer() {
      return this.renderService.getUniqueId('countylayer', this.id);
    },
    zipSource() {
      return this.renderService.getUniqueId('ziplinesource', this.id);
    },
    zipLayer() {
      return this.renderService.getUniqueId('ziplinelayer', this.id);
    },
    dMASource() {
      return this.renderService.getUniqueId('dmasource', this.id);
    },
    dMALayer() {
      return this.renderService.getUniqueId('dmalayer', this.id);
    },
    congressionalDistrictSource() {
      return this.renderService.getUniqueId('congressionaldistrictsource', this.id);
    },
    congressionalDistrictLayer() {
      return this.renderService.getUniqueId('congressionaldistrictlayer', this.id);
    },
    metricColumnsExpression() {
      return ['at', 0, ['get', 'columns']];
    },
    metricRawValueExpression() {
      return ['get', 'rawField', this.metricColumnsExpression];
    },
    metricSymbolExpression() {
      return ['get', 'symbol', this.metricColumnsExpression];
    },
    metricSymbolPrependExpression() {
      return ['get', 'prepend', this.metricColumnsExpression];
    },
    metricExpression() {
      return [
        'case',
        ['>=', this.metricRawValueExpression, 1000], // If value >= 1000
        ['concat', ['/', ['round', ['/', this.metricRawValueExpression, 100]], 10], 'k'],
        ['concat', ['to-string', ['/', ['round', ['*', this.metricRawValueExpression, 10]], 10]]],
      ];
    },
    metricClusterTotalValueExpression() {
      return ['get', 'totalValue'];
    },
    metricClusterSymbolExpression() {
      return [
        'case',
        ['>=', this.metricClusterTotalValueExpression, 1000], // If value >= 1000
        ['concat', ['/', ['round', ['/', this.metricClusterTotalValueExpression, 100]], 10], 'k'],
        [
          'concat',
          ['to-string', ['/', ['round', ['*', this.metricClusterTotalValueExpression, 10]], 10]],
        ],
      ];
    },
    metricClusterExpression() {
      return [
        'case',
        ['!=', ['get', 'symbol'], null],
        [
          'case',
          ['==', ['get', 'prepend'], true],
          ['concat', ['get', 'symbol'], this.metricClusterSymbolExpression],
          ['concat', this.metricClusterSymbolExpression, ['get', 'symbol']],
        ],
        this.metricClusterSymbolExpression,
      ];
    },
  },
  methods: {
    getLayerName(name) {
      return this.renderService.getUniqueId(name, this.id);
    },
    getUniqueId(name) {
      return this.renderService.getUniqueId(name, this.id);
    },
    async autoZoomFlow(sourceId, data, serviceObject) {
      const map = this.map;
      const rawData = serviceObject.getData(data);
      const filteredData = serviceObject.filterData(rawData);
      if (!filteredData.length) return;

      await MapboxTileSetStorage.loadAllTiles(serviceObject, sourceId, map);

      const filters = serviceObject.getFilters(filteredData);
      try {
        const features = map.querySourceFeatures(sourceId, {
          sourceLayer: serviceObject.getConfig('SOURCE_LAYER'),
          filter: filters,
        });
        if (!features.length) return;

        const bounds = new mapboxgl.LngLatBounds();
        for (const feature of features) {
          const geom = feature.geometry;
          if (!geom) continue;

          const coords = MapboxFeatureBoundsHandler.getAllCoordinates(geom);
          coords.forEach((c) => bounds.extend(c));
        }
        const layer = this.map.getSource(sourceId);
        const layerMinZoom = (layer?.minzoom ?? 1) + 0.1;
        const layerMaxZoom = (layer?.maxzoom ?? 24) - 0.1;

        this.map.customTapMap = {
          minZoom: layerMinZoom,
        };

        // If the area is too large, force a zoom cap
        this.map.fitBounds(bounds, {
          maxZoom: layerMaxZoom,
          minZoom: layerMinZoom,
        });
      } catch (_) {
        return [];
      }
    },
    resetMap() {
      this.resetMetricLatLong();
      this.resetLatLong();
      this.resetCountry();
      this.resetState();
      this.resetCounty();
      this.resetCity();
      this.resetZipLine();
      this.resetDMA();
      this.resetCongressionalDistrict();
    },
    resetLatLong() {
      if (this.map.getLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_CLUSTER_LAYER))) {
        this.map.removeLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_CLUSTER_LAYER));
      }
      if (this.map.getLayer(this.latLongUnClusterLayer)) {
        this.map.removeLayer(this.latLongUnClusterLayer);
      }
      if (this.map.getLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_CLUSTER_COUNT_LAYER))) {
        this.map.removeLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_CLUSTER_COUNT_LAYER));
      }
      if (this.map.getSource(this.latLongSource)) {
        this.map.removeSource(this.latLongSource);
      }
    },
    resetMetricLatLong() {
      if (this.map.getLayer(this.symbolLayer)) {
        this.map.removeLayer(this.symbolLayer);
      }
      if (this.map.getLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_METRIC_UNCLUSTER_LAYER))) {
        this.map.removeLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_METRIC_UNCLUSTER_LAYER));
      }
      if (this.map.getLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_METRIC_CLUSTER_COUNT_LAYER))) {
        this.map.removeLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_METRIC_CLUSTER_COUNT_LAYER));
      }
      if (
        this.map.getLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_METRIC_CLUSTER_CIRCLE_LAYER))
      ) {
        this.map.removeLayer(this.getLayerName(MAPBOX_LAYERS.LAT_LONG_METRIC_CLUSTER_CIRCLE_LAYER));
      }
      if (this.map.getSource(this.latLongMetricSource)) {
        this.map.removeSource(this.latLongMetricSource);
      }
    },
    resetState() {
      if (this.map.getLayer(this.stateLayer)) {
        this.map.removeLayer(this.stateLayer);
      }
      if (this.map.getSource(this.stateSource)) {
        this.map.removeSource(this.stateSource);
      }
    },
    resetCity() {
      if (this.map.getLayer(this.cityLayer)) {
        this.map.removeLayer(this.cityLayer);
      }
      if (this.map.getSource(this.citySource)) {
        this.map.removeSource(this.citySource);
      }
    },
    resetCountry() {
      if (this.map.getLayer(this.countryLayer)) {
        this.map.removeLayer(this.countryLayer);
      }
      if (this.map.getSource(this.countrySource)) {
        this.map.removeSource(this.countrySource);
      }
    },
    resetZipLine() {
      if (this.map.getLayer(this.zipLayer)) {
        this.map.removeLayer(this.zipLayer);
      }
      if (this.map.getSource(this.zipSource)) {
        this.map.removeSource(this.zipSource);
      }
    },
    resetCounty() {
      if (this.map.getLayer(this.countyLayer)) {
        this.map.removeLayer(this.countyLayer);
      }
      if (this.map.getSource(this.countySource)) {
        this.map.removeSource(this.countySource);
      }
    },
    resetDMA() {
      if (this.map.getLayer(this.dMALayer)) {
        this.map.removeLayer(this.dMALayer);
      }
      if (this.map.getSource(this.dMASource)) {
        this.map.removeSource(this.dMASource);
      }
    },
    resetCongressionalDistrict() {
      if (this.map.getLayer(this.congressionalDistrictLayer)) {
        this.map.removeLayer(this.congressionalDistrictLayer);
      }
      if (this.map.getSource(this.congressionalDistrictSource)) {
        this.map.removeSource(this.congressionalDistrictSource);
      }
    },
    getMatchData() {
      const firstSelectedColumn = first(this.renderService.getSelectedColumns(this.widget));
      const groupedColumn = MapboxRenderService.getSelectedColumn(this.widget);
      const cachedData = [];
      const nonBoundaryData = [];
      const boundaryData = [];
      const columnGradients = MapboxRenderService.getColumnGradients(
        this.getData(),
        firstSelectedColumn.field
      );
      for (let j = 0; j < this.getData().length; j++) {
        const row = this.getData()[j];
        const color = MapboxRenderService.getGradientColor(
          firstSelectedColumn.color ||
            (this.widget.metadata.chart_palette && this.widget.metadata.chart_palette[0]) ||
            this.chartPalette[0],
          MapboxRenderService.getColumnGradientValue(
            columnGradients,
            row[firstSelectedColumn.field]
          )
        );
        let value = row[groupedColumn];
        if (
          this.renderService.canShowCountryLayer(this.widget) &&
          MapboxRenderService.isValidGeoConfig(row.geocode0)
        ) {
          value = MapboxRenderService.getValueFromGeoConfig(row.geocode0);
        }
        if (
          this.renderService.canShowStateLayer(this.widget) &&
          MapboxRenderService.isValidGeoConfig(row.geocode1)
        ) {
          value = MapboxRenderService.getValueFromGeoConfig(row.geocode1);
        }
        if (
          this.renderService.canShowCountyLayer(this.widget) &&
          MapboxRenderService.isValidGeoConfig(row.geocode2)
        ) {
          value = MapboxRenderService.getValueFromGeoConfig(row.geocode2);
        }
        if (value && !cachedData.includes(value.toUpperCase())) {
          cachedData.push(value.toUpperCase());
          boundaryData.push(value.toUpperCase(), BLUE);
          nonBoundaryData.push(value.toUpperCase(), color);
        }
      }
      return [boundaryData, nonBoundaryData];
    },
    getCityMatchData(isBoundary = false) {
      const cachedData = {};
      const matchData = {};
      const firstSelectedColumn = first(this.renderService.getSelectedColumns(this.widget));
      const groupedColumn = MapboxRenderService.getSelectedColumn(this.widget);
      const columnGradients = MapboxRenderService.getColumnGradients(
        this.getData(),
        firstSelectedColumn.field
      );
      for (let j = 0; j < this.getData().length; j++) {
        const row = this.getData()[j];
        let color = BLUE;
        if (!isBoundary) {
          color = MapboxRenderService.getGradientColor(
            firstSelectedColumn.color ||
              (this.widget.metadata.chart_palette && this.widget.metadata.chart_palette[0]) ||
              this.chartPalette[0],
            MapboxRenderService.getColumnGradientValue(
              columnGradients,
              row[firstSelectedColumn.field]
            )
          );
        }
        let value = row[groupedColumn];
        let stateDetails = value.split('-');
        if (stateDetails.length < 3) {
          const stateJoin = row.geocode1;
          if (stateJoin) {
            stateDetails = stateJoin.split('-');
          }
        }
        if (stateDetails.length >= 3) {
          [, , value] = stateDetails;
        }
        if (stateDetails.length >= 2) {
          if (!cachedData[stateDetails[1]]) {
            cachedData[stateDetails[1]] = [];
          }
          if (value && !cachedData[stateDetails[1]].includes(value)) {
            cachedData[stateDetails[1]].push(value);
            if (!matchData[stateDetails[1]]) {
              matchData[stateDetails[1]] = [];
            }
            matchData[stateDetails[1]].push(value.toUpperCase(), color);
          }
        }
      }
      return matchData;
    },
    tileSetColor(fields) {
      fields = isArray(fields) ? fields : [fields];
      const [boundaryMatchData, matchData] = this.getMatchData();
      if (isEmpty(matchData) && isEmpty(boundaryMatchData)) {
        return ['rgba(255,255,255,0)', 'rgba(255,255,255,0)'];
      }
      let boundaryMatchExpression = [];
      let matchExpression = [];

      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        const expression = ['match', ['upcase', ['get', field]], ...matchData];
        const boundaryExpression = ['match', ['upcase', ['get', field]], ...boundaryMatchData];

        if (i === fields.length - 1) {
          expression.push('rgba(255, 255, 255, 0)');
          boundaryExpression.push('rgba(255, 255, 255, 0)');
        }

        if (matchExpression.length === 0) {
          matchExpression = expression;
        } else {
          let pre = matchExpression;
          let last = _.last(matchExpression);
          while (isArray(last)) {
            pre = last;
            last = _.last(last);
          }
          pre.push(expression);
        }

        if (boundaryMatchExpression.length === 0) {
          boundaryMatchExpression = boundaryExpression;
        } else {
          let pre = boundaryMatchExpression;
          let last = _.last(boundaryMatchExpression);
          while (isArray(last)) {
            pre = last;
            last = _.last(last);
          }
          pre.push(boundaryExpression);
        }
      }
      return [boundaryMatchExpression, matchExpression];
    },
    updateLatLongLayer() {
      if (this.renderService.canShowLatLongLayerWithMetricValue(this.widget)) {
        this.map.addSource(this.latLongMetricSource, {
          type: 'geojson',
          data: this.mapboxData,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
          clusterProperties: {
            symbol: ['coalesce', ['get', 'symbol', ['at', 0, ['get', 'columns']]], null],
            prepend: ['coalesce', ['get', 'prepend', ['at', 0, ['get', 'columns']]], null],
            totalValue: ['+', this.metricRawValueExpression],
          },
        });
        this.map.addLayer({
          id: this.getLayerName(MAPBOX_LAYERS.LAT_LONG_METRIC_UNCLUSTER_LAYER),
          type: 'circle',
          source: this.latLongMetricSource,
          filter: ['!has', 'point_count'],
          paint: {
            'circle-color':
              first(this.renderService.getSelectedColumns(this.widget)).color ||
              (this.widget.metadata.chart_palette && this.widget.metadata.chart_palette[0]) ||
              this.chartPalette[0] ||
              '#F72119',
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['round', ['log10', this.metricRawValueExpression]],
              0,
              10,
              1,
              12,
              2,
              14,
              3,
              16,
              4,
              18,
              5,
              20,
              6,
              22,
              7,
              24,
              8,
              26,
              9,
              28,
              10,
              30,
              11,
              32,
              12,
              34,
              13,
              36,
              14,
              38,
              15,
              39,
              16,
              40,
              18,
              41,
              19,
              42,
              20,
              43,
            ],
            'circle-opacity': 0.6,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
          },
        });
        this.map.addLayer({
          id: this.getLayerName(MAPBOX_LAYERS.LAT_LONG_METRIC_CLUSTER_CIRCLE_LAYER),
          type: 'circle',
          source: this.latLongMetricSource,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'interpolate',
              ['linear'],
              ['/', this.metricClusterTotalValueExpression, 100],
              40,
              '#51bbd6',
              60,
              '#f1f075',
              80,
              '#f28cb1',
            ],
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['round', ['log10', this.metricClusterTotalValueExpression]],
              0,
              10,
              1,
              12,
              2,
              14,
              3,
              16,
              4,
              18,
              5,
              20,
              6,
              22,
              7,
              24,
              8,
              26,
              9,
              28,
              10,
              30,
              11,
              32,
              12,
              34,
              13,
              36,
              14,
              38,
              15,
              39,
              16,
              40,
              18,
              41,
              19,
              42,
              20,
              43,
            ],
          },
        });
        this.map.addLayer({
          id: this.getLayerName(MAPBOX_LAYERS.LAT_LONG_METRIC_CLUSTER_COUNT_LAYER),
          type: 'symbol',
          source: this.latLongMetricSource,
          filter: ['has', 'point_count'],
          layout: {
            'icon-allow-overlap': true, // Allow icon overlap
            'text-allow-overlap': true,
            'text-field': this.metricClusterExpression,
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-size': [
              'interpolate',
              ['linear'],
              ['log10', ['get', 'totalValue']],
              0,
              7,
              1,
              8,
              2,
              10,
              3,
              12,
              4,
              13,
              5,
              14,
              6,
              15,
              8,
              16,
            ],
          },
          paint: {
            'text-color': 'black',
          },
        });
        this.map.addLayer({
          id: this.symbolLayer,
          type: 'symbol',
          source: this.latLongMetricSource,
          filter: ['!has', 'point_count'],
          layout: {
            'icon-allow-overlap': true, // Allow icon overlap
            'text-allow-overlap': true,
            'text-field': [
              'case',
              ['!=', this.metricSymbolExpression, null],
              [
                'case',
                ['==', this.metricSymbolPrependExpression, true],
                ['concat', this.metricSymbolExpression, this.metricExpression],
                ['concat', this.metricExpression, this.metricSymbolExpression],
              ],
              this.metricExpression,
            ],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-size': [
              'interpolate',
              ['linear'],
              ['log10', ['get', 'rawField', ['at', 0, ['get', 'columns']]]],
              0,
              7,
              1,
              8,
              2,
              10,
              3,
              12,
              4,
              13,
              5,
              14,
              6,
              15,
              8,
              16,
            ],
          },
          paint: {
            'text-color': 'black',
          },
        });
      }
      if (this.renderService.canShowDefaultLatLongLayer(this.widget)) {
        this.map.addSource(this.latLongSource, {
          type: 'geojson',
          data: this.mapboxData,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
        });
        this.map.addLayer({
          id: this.getLayerName(MAPBOX_LAYERS.LAT_LONG_CLUSTER_LAYER),
          type: 'circle',
          source: this.latLongSource,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'step',
              ['get', 'point_count'],
              '#51bbd6',
              100,
              '#f1f075',
              750,
              '#f28cb1',
            ],
            'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
          },
        });
        this.map.addLayer({
          id: this.getLayerName(MAPBOX_LAYERS.LAT_LONG_CLUSTER_COUNT_LAYER),
          type: 'symbol',
          source: this.latLongSource,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-size': 12,
          },
        });
        this.map.addLayer({
          id: this.latLongUnClusterLayer,
          type: 'circle',
          source: this.latLongSource,
          filter: ['!has', 'point_count'],
          paint: {
            'circle-color':
              first(this.renderService.getSelectedColumns(this.widget)).color ||
              (this.widget.metadata.chart_palette && this.widget.metadata.chart_palette[0]) ||
              this.chartPalette[0] ||
              '#F72119',
            'circle-radius': 4,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
          },
        });
      }
    },
    updateCountryLayer() {
      if (this.renderService.canShowCountryLayer(this.widget)) {
        const { SOURCE_LAYER, FIELDS } = MAPBOX_LAYERS_METADATA[COUNTRY];
        this.map.addSource(this.countrySource, {
          type: 'vector',
          url: 'mapbox://mapbox.country-boundaries-v1',
        });
        const [, fillColor] = this.tileSetColor(FIELDS);
        this.map.addLayer(
          {
            id: this.countryLayer,
            source: this.countrySource,
            'source-layer': SOURCE_LAYER,
            type: 'fill',
            paint: {
              'fill-color': fillColor,
              'fill-opacity': this.getLayerOpacity(),
            },
            filter: ['has', 'iso_3166_1_alpha_3'],
          },
          this.getSymbolLayer()
        );
      }
    },
    updateStateLayer() {
      if (this.renderService.canShowStateLayer(this.widget)) {
        const { SOURCE_LAYER, FIELDS } = MAPBOX_LAYERS_METADATA[STATE];
        this.map.addSource(this.stateSource, {
          type: 'vector',
          url: 'mapbox://tapclicks.cwo7b88z',
        });
        const [fillOutlineColor, fillColor] = this.tileSetColor(FIELDS);
        this.map.addLayer(
          {
            id: this.stateLayer,
            type: 'fill',
            source: this.stateSource,
            'source-layer': SOURCE_LAYER,
            paint: {
              'fill-outline-color': fillOutlineColor,
              'fill-color': fillColor,
              'fill-opacity': this.getLayerOpacity(),
            },
          },
          this.getSymbolLayer()
        );
      }
    },
    updateCountyLayer() {
      if (this.renderService.canShowCountyLayer(this.widget)) {
        this.map.addSource(this.countySource, {
          type: 'vector',
          url: 'mapbox://tapclicks.1e1hc39s',
        });
        const [fillOutlineColor, fillColor] = this.tileSetColor(['NAME', 'NAMELSAD']);
        this.map.addLayer(
          {
            id: this.countyLayer,
            type: 'fill',
            source: this.countySource,
            'source-layer': 'tl_rd22_us_county-0pz5qq',
            paint: {
              'fill-outline-color': fillOutlineColor,
              'fill-color': fillColor,
              'fill-opacity': this.getLayerOpacity(),
            },
          },
          this.getSymbolLayer()
        );
      }
    },
    cityTileSetColor(fields, isBoundary = false) {
      fields = isArray(fields) ? fields : [fields];
      const matchData = this.getCityMatchData(isBoundary);
      if (isEmpty(matchData)) {
        return 'rgba(255, 255, 255,0)';
      }

      const stateExpression = ['match', ['upcase', ['get', 'STUSPS']]];
      let matchExpression = [];
      forEach(matchData, (cityInfo, state) => {
        matchExpression = [];
        for (let i = 0; i < fields.length; i++) {
          const field = fields[i];
          const expression = ['match', ['upcase', ['get', field]], ...cityInfo];
          if (i === fields.length - 1) {
            expression.push('rgba(255, 255, 255, 0)');
          }

          if (matchExpression.length === 0) {
            matchExpression = expression;
          } else {
            let pre = matchExpression;
            let last = _.last(matchExpression);
            while (isArray(last)) {
              pre = last;
              last = _.last(last);
            }
            pre.push(expression);
          }
        }
        stateExpression.push(state.toUpperCase(), matchExpression);
      });
      stateExpression.push('rgba(255, 255, 255, 0)');
      return stateExpression;
    },
    updateCityLayer() {
      if (this.renderService.canShowCityLayer(this.widget)) {
        this.map.addSource(this.citySource, {
          type: 'vector',
          url: 'mapbox://tapclicks.2sp9ab9u',
        });
        this.map.addLayer(
          {
            id: this.cityLayer,
            type: 'fill',
            source: this.citySource,
            'source-layer': 'cb_2022_us_place_500k-52iz77',
            paint: {
              'fill-outline-color': this.cityTileSetColor(['NAME', 'STUSPS', 'NAMELSAD'], true),
              'fill-color': this.cityTileSetColor(['NAME', 'STUSPS', 'NAMELSAD']),
              'fill-opacity': this.getLayerOpacity(),
            },
          },
          this.getSymbolLayer()
        );
      }
    },
    updateZipLayer() {
      if (this.renderService.canShowZipCodeLayer(this.widget)) {
        this.map.addSource(this.zipSource, {
          type: 'vector',
          url: 'mapbox://tapclicks.blqzgepw',
        });
        const [fillOutlineColor, fillColor] = this.tileSetColor('NAME20');
        this.map.addLayer(
          {
            id: this.zipLayer,
            type: 'fill',
            source: this.zipSource,
            'source-layer': 'cb_2020_us_zcta520_500k-a84xva',
            paint: {
              'fill-outline-color': fillOutlineColor,
              'fill-color': fillColor,
              'fill-opacity': this.getLayerOpacity(),
            },
            filter: ['has', 'NAME20'],
          },
          this.getSymbolLayer()
        );
      }
    },
    updateDMALayer() {
      if (this.renderService.canShowDmaLayer(this.widget)) {
        this.map.addSource(this.dMASource, {
          type: 'vector',
          url: 'mapbox://tapclicks.3f7wh1ra',
        });
        const [fillOutlineColor, fillColor] = this.tileSetColor(['NAME', 'Key']);
        this.map.addLayer(
          {
            id: this.dMALayer,
            type: 'fill',
            source: this.dMASource,
            'source-layer': 'new_dma-9hgg7m',
            paint: {
              'fill-outline-color': fillOutlineColor,
              'fill-color': fillColor,
              'fill-opacity': this.getLayerOpacity(),
            },
          },
          this.getSymbolLayer()
        );
      }
    },
    updateCongressionalDistrictLayer() {
      if (this.renderService.canShowCongressionalDistrictLayer(this.widget)) {
        const metadata = MAPBOX_LAYERS_METADATA[CONGRESSIONAL_DISTRICT];
        this.map.addSource(this.congressionalDistrictSource, {
          type: 'vector',
          url: 'mapbox://tapclicks.cju5nw9b',
        });
        const [fillOutlineColor, fillColor] = this.tileSetColor(metadata.FIELDS);
        this.map.addLayer(
          {
            id: this.congressionalDistrictLayer,
            type: 'fill',
            source: this.congressionalDistrictSource,
            'source-layer': metadata.SOURCE_LAYER,
            paint: {
              'fill-outline-color': fillOutlineColor,
              'fill-color': fillColor,
              'fill-opacity': this.getLayerOpacity(),
            },
          },
          this.getSymbolLayer()
        );
      }
    },
    handleLayerHover(event, values, hoveredName) {
      const dataColumns = this.renderService.getSelectedColumns(this.widget);
      const temp = {};
      forEach(values, (val) => {
        forEach(dataColumns, (column) => {
          if (temp[column.field]) {
            temp[column.field] += Number(val[column.field]);
          } else {
            temp[column.field] = Number(val[column.field]);
          }
        });
      });

      this.popUp
        .setLngLat(event.lngLat)
        .setHTML(
          this.renderService.getPopupHtml({
            grouped: JSON.stringify({
              field: hoveredName,
            }),
            columns: JSON.stringify(
              map(dataColumns, (column, index) => ({
                field: NumeralService.formatValue(
                  temp[column.field],
                  column.format,
                  column.precision,
                  column.isCompactNumber,
                  column.currencySymbol
                ),
                label: column.label,
                color:
                  column.color ||
                  this.renderService.getPaletteColor(
                    index,
                    this.widget.metadata.chart_palette || this.chartPalette
                  ),
              }))
            ),
          })
        )
        .addTo(this.map);
    },
    getSymbolLayer() {
      if (this.widget.metadata.draw_options.map_geographic_features) {
        return null;
      }
      const { layers } = this.map.getStyle();
      return find(layers, (layer) => layer.type === SYMBOL)?.id;
    },
    getLayerOpacity() {
      const { geo_layer_opacity } = this.widget.metadata.draw_options;

      if (!geo_layer_opacity) {
        return 1;
      }

      return geo_layer_opacity / 100;
    },
  },
};
